/* @flow */
import _ from 'lodash';

import type { ReduxState, Action, Fetchable, SimplifiedUsage, Usage } from '../../types';

// ===========================
//   STATE TYPE
// ===========================
export type UsagesState = {
  usages: Fetchable<SimplifiedUsage[]>,
  usage: Fetchable<Usage>,
  selectedUsageId?: ?number,
  sessionUsageId: Fetchable<number | void>,
};

// ===========================
//   INITIAL STATE
// ===========================
export const initialState: UsagesState = {
  usages: { fetching: false },
  usage: { fetching: false },
  sessionUsageId: { fetching: false },
};

// ===========================
//   REDUCER
// ===========================
export default (state: UsagesState = initialState, action: Action): UsagesState => {
  switch (action.type) {
    // USAGE_LIST
    case 'FETCH_USAGE_LIST_REQUEST': {
      return {
        ...state,
        usages: { fetching: true },
      };
    }
    case 'FETCH_USAGE_LIST_SUCCESS': {
      const data = action.payload;
      return {
        ...state,
        usages: { fetching: false, data },
      };
    }
    case 'FETCH_USAGE_LIST_FAILURE': {
      const error = action.payload;
      return {
        ...state,
        usages: { fetching: false, error },
      };
    }
    // USAGE
    case 'CREATE_USAGE': {
      return {
        ...state,
        selectedUsageId: null,
        usage: { fetching: false },
      };
    }
    case 'DELETE_USAGE_REQUEST':
    case 'SAVE_USAGE_REQUEST': {
      return {
        ...state,
        usage: { fetching: true, data: state.usage.data },
      };
    }
    case 'FETCH_USAGE_REQUEST': {
      return {
        ...state,
        usage: { fetching: true },
      };
    }
    case 'DELETE_USAGE_FAILURE':
    case 'SAVE_USAGE_FAILURE':
    case 'FETCH_USAGE_FAILURE': {
      const error = action.payload;
      return {
        ...state,
        usage: { fetching: false, error },
      };
    }
    case 'FETCH_USAGE_SUCCESS': {
      const data = action.payload;
      return {
        ...state,
        selectedUsageId: data.id,
        usage: { fetching: false, data },
      };
    }
    case 'SAVE_USAGE_SUCCESS': {
      const data = action.payload;
      return {
        ...state,
        usage: { fetching: false, data },
      };
    }
    case 'DELETE_USAGE_SUCCESS': {
      const deletedUsageId = action.payload;
      const currentUsageId = _.get(state.usage.data, 'id');
      return {
        ...state,
        sessionUsageId: deletedUsageId === state.sessionUsageId ? undefined : state.sessionUsageId,
        usage: {
          fetching: false,
          data: deletedUsageId === currentUsageId ? undefined : state.usage.data,
        },
        usages: {
          fetching: false,
          data: state.usages.data && state.usages.data.filter(usage => usage.id !== deletedUsageId),
        },
      };
    }
    // SESSION_USAGE
    case 'GET_SESSION_USAGE_ID_SUCCESS':
    case 'SET_SESSION_USAGE_ID_SUCCESS': {
      const data = action.payload;
      return {
        ...state,
        sessionUsageId: { fetching: false, data },
      };
    }
    case 'SET_SESSION_USAGE_ID_FAILURE':
    case 'GET_SESSION_USAGE_ID_FAILURE': {
      const error = action.payload;
      return {
        ...state,
        sessionUsageId: { fetching: false, error },
      };
    }
    case 'LOGOUT_SUCCESS':
      return initialState;
    default: {
      return state;
    }
  }
};

// ===========================
//   SELECTORS
// ===========================

export const getIsLoadingUsages = (state: ReduxState): boolean => {
  return !!(state.usages.usages.fetching || state.usages.usage.fetching);
};

export const getUsages = (state: ReduxState): Fetchable<SimplifiedUsage[]> => {
  return state.usages.usages;
};

export const getSelectedUsageId = (state: ReduxState): ?number | void => {
  return state.usages.selectedUsageId;
};

export const getUsage = (state: ReduxState): Fetchable<Usage> => {
  return state.usages.usage;
};

export const getSessionUsageId = (state: ReduxState): Fetchable<number | void> => {
  return state.usages.sessionUsageId;
};
